import { Box, Button, Flex, type ToastId, useToast as useChakraToast } from '@chakra-ui/react'
import { Duration } from 'luxon'
import { useCallback, useEffect, useRef } from 'react'

import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import ephemeralStorage from '@/client/services/browserStorage/ephemeralStorage'
import { getSettingsUrl } from '@/client/services/url'

import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'

// Keep a non-component, global flag for this, because we dont
// want to reprompt for update on a navigation event.
let hasPresentedToast = false
// Chakra does not offer dismiss / regular close, so to avoid handling
// dismissal twice, we have to keep a global flag
let hasDismissedToast = false

export const COOKIE_NAME = 'shw-pref-toast-dismissed-missing-phone'

const HOURS_BEFORE_SHOWING_TOAST_TO_NEW_USERS = 1

export default function MissingPhoneNumberToast() {
  const toast = useToaster()
  const chakraToast = useChakraToast()
  // ^ Need this for the dismissing stuff :(

  const query = useQuery()
  const currentUser = useUser()
  const toastIdRef = useRef<ToastId | null>(null)

  const me = query.one(UserQ.where({ id: currentUser.id }))
  const hasPhoneNumber = me<boolean>`${UserQ.phone_number} IS NOT NULL` ?? false
  const hasPhoneNumberWaived = me<boolean>`${UserQ.phone_number_waived_at} IS NOT NULL` ?? false
  const isBrandNewUser =
    me<boolean>`${UserQ.created_at} > NOW() - ${HOURS_BEFORE_SHOWING_TOAST_TO_NEW_USERS} * INTERVAL '1 HOUR'` ??
    false

  const eligibleForToast = !hasPhoneNumber && !hasPhoneNumberWaived && !isBrandNewUser

  const handleDismiss = useCallback(
    (remember: boolean) => {
      if (remember && !hasDismissedToast) {
        ephemeralStorage.set(COOKIE_NAME, 'y', {
          duration: Duration.fromObject({ days: 3 }),
        })
      }
      if (toastIdRef.current != null) {
        chakraToast.close(toastIdRef.current)
      }
      toastIdRef.current = null
      hasDismissedToast = true
    },
    [chakraToast]
  )

  const tryPresenting = useCallback(() => {
    if (hasPresentedToast) {
      return
    }
    hasPresentedToast = true

    toastIdRef.current = toast({
      description: (
        <Flex direction="row">
          <Box>
            Get text message reminders before every session! Add your phone number to receive
            alerts.
          </Box>
          <Flex justifyContent="center" alignItems="center" mx="8px">
            <ShwLink href={`${getSettingsUrl()}?open=phone-number`}>
              <Button onClick={() => handleDismiss(false)} bgColor="info.solid">
                Add Phone
              </Button>
            </ShwLink>
          </Flex>
        </Flex>
      ),
      status: 'info',
      duration: null,

      onCloseComplete: () => handleDismiss(true),
    })
  }, [toast, handleDismiss])

  useEffect(() => {
    const dismissed = ephemeralStorage.get(COOKIE_NAME) === 'y'
    hasDismissedToast = hasDismissedToast || dismissed

    if (eligibleForToast && !hasDismissedToast) {
      // We add a delay so the toast container has time to mount
      setTimeout(tryPresenting, 1000)
    }
  }, [eligibleForToast, tryPresenting])

  return null
}
